import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import {
  catchError,
  concat,
  defer,
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  filter,
  from,
  map,
  of,
  pairwise,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { IAuthUser } from '@core/interfaces/auth-user.interface';
import { AuthApiService } from '@core/api/auth-api.service';
import { localReturnPath } from '@core/services/auth/auth-return-path';
import { APPLICATION_SESSION } from '@core/services/auth/application-session';
import { NativeShellService } from '@core/services/native-shell/native-shell.service';
import { sameApplicationSession } from '@core/services/auth/access-claims';
import {
  APP_NAVIGATION_CONTEXT_DESCRIPTORS,
  APP_ROUTE_REQUIRED_ACCESS_DATA_KEY,
  AppAccessRequirement,
  hasRequiredAccess,
} from '@core/models/app-navigation.model';
import { ExternalNavigationService } from '@core/services/external-navigation.service';
import { AuthActions } from './auth.actions';
import { selectAuth } from './auth.selectors';

@Injectable()
export class AuthEffects {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);
  private readonly session = inject(APPLICATION_SESSION);
  private readonly shell = inject(NativeShellService);
  private readonly api = inject(AuthApiService);
  private readonly router = inject(Router);
  private readonly externalNavigation = inject(ExternalNavigationService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly nativeSession$ = createEffect(() =>
    this.session.state$.pipe(
      distinctUntilChanged(sameApplicationSession),
      map((session) => AuthActions.sessionChanged({ session })),
    ),
  );

  readonly sessionEnded$ = createEffect(
    () =>
      this.session.state$.pipe(
        map((session) => session.authenticated),
        distinctUntilChanged(),
        pairwise(),
        filter(([before, after]) => before && !after && !this.session.ending),
        tap(
          () => void this.router.navigate(['/auth-code'], { replaceUrl: true }),
        ),
      ),
    { dispatch: false },
  );

  readonly synchronize$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.sessionChanged),
      withLatestFrom(this.store.select(selectAuth)),
      switchMap(([{ session }, auth]) => {
        if (!session.authenticated || !session.subject) return EMPTY;
        const baseline = auth.synchronizationBaseline;
        const completion = (user: IAuthUser | null, failed: boolean) =>
          AuthActions.sessionSynchronizationCompleted({
            subject: session.subject,
            user,
            access: session.access,
            identityChanged: failed || baseline?.subject !== session.subject,
            accessChanged: !sameAccess(baseline?.access ?? [], session.access),
            accessLoadFailed: failed,
            initial: baseline?.initial ?? true,
          });
        if (session.isSecurityAdministrator || !session.access.length)
          return of(completion(null, false));
        return this.api.getCurrentUserInfo().pipe(
          map((user) => completion(user, !user)),
          catchError(() => of(completion(null, true))),
          takeUntil(
            this.actions.pipe(
              ofType(AuthActions.logout, AuthActions.sessionFailed),
            ),
          ),
        );
      }),
    ),
  );

  readonly reconcile$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.sessionSynchronizationCompleted),
      withLatestFrom(this.store.select(selectAuth)),
      filter(([action, state]) => state.subject === action.subject),
      switchMap(
        ([
          { initial, identityChanged, accessChanged, accessLoadFailed, access },
          state,
        ]) => {
          if (initial) return of(AuthActions.sessionSynchronizationReleased());
          const currentUrl = new URL(this.router.url, location.origin);
          const isExplanation =
            currentUrl.pathname === '/security-administrator';
          const returnPath = isExplanation
            ? localReturnPath(currentUrl.searchParams.get('returnPath'))
            : null;
          if (
            !state.isSecurityAdministrator &&
            !isExplanation &&
            !accessLoadFailed &&
            !identityChanged &&
            (!accessChanged ||
              activeRouteAllowsAccess(
                this.router.routerState.snapshot.root,
                access,
              ))
          ) {
            return of(AuthActions.sessionSynchronizationReleased());
          }
          const target = APP_NAVIGATION_CONTEXT_DESCRIPTORS.find(
            ({ access: required }) => hasRequiredAccess(access, required),
          );
          return concat(
            of(AuthActions.sessionSynchronizationRedirecting()),
            defer(() => {
              this.externalNavigation.replace(
                state.isSecurityAdministrator
                  ? '/security-administrator?' +
                      new URLSearchParams({ returnPath: this.router.url })
                  : accessLoadFailed
                    ? '/access-unavailable'
                    : (returnPath ?? target?.route ?? '/forbidden'),
              );
              return EMPTY;
            }),
          );
        },
      ),
    ),
  );

  readonly retryAccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.requestAccess),
      exhaustMap(() =>
        defer(() => this.session.refreshSession()).pipe(
          map((session) => AuthActions.sessionChanged({ session })),
          takeUntil(
            this.actions.pipe(
              ofType(AuthActions.sessionChanged, AuthActions.logout),
            ),
          ),
          catchError(() =>
            of(
              AuthActions.sessionFailed({
                message: 'GENERAL.AUTH_UNAVAILABLE',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly authorize$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.authorize),
      exhaustMap(({ returnPath }) =>
        defer(() => this.session.login(returnPath)).pipe(
          switchMap(() => EMPTY),
          catchError(() =>
            of(
              AuthActions.sessionFailed({
                message: 'GENERAL.AUTH_UNAVAILABLE',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly logout$ = createEffect(() =>
    this.actions.pipe(
      ofType(AuthActions.logout),
      exhaustMap(({ returnPath }) =>
        from(this.session.logout(returnPath)).pipe(
          tap(() => {
            if (this.shell.present)
              void this.router.navigate(['/auth-code'], { replaceUrl: true });
          }),
          switchMap(() => EMPTY),
          catchError(() => {
            if (this.shell.present)
              void this.router.navigate(['/auth-code'], { replaceUrl: true });
            return of(
              AuthActions.sessionFailed({
                message: 'GENERAL.AUTH_UNAVAILABLE',
              }),
            );
          }),
        ),
      ),
    ),
  );

  readonly showError$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(AuthActions.sessionFailed),
        tap(({ message }) =>
          this.toast.add({
            level: 'error',
            summary: this.translate.instant('GENERAL.ERROR_REQUEST'),
            detail: this.translate.instant(message),
          }),
        ),
      ),
    { dispatch: false },
  );
}

function sameAccess(
  previous: readonly string[],
  current: readonly string[],
): boolean {
  return (
    previous.length === current.length &&
    previous.every((code) => current.includes(code))
  );
}

export function activeRouteAllowsAccess(
  root: ActivatedRouteSnapshot,
  access: readonly string[],
): boolean {
  let route: ActivatedRouteSnapshot | null = root;
  let authenticatedShell = false;
  while (route) {
    if (route.data['bottomChrome'] === true) authenticatedShell = true;
    const required = route.data[APP_ROUTE_REQUIRED_ACCESS_DATA_KEY] as
      | AppAccessRequirement
      | undefined;
    if (required && !hasRequiredAccess(access, required)) return false;
    route = route.firstChild;
  }
  return authenticatedShell;
}
